import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient as qc } from '@/lib/query';
import {
  InvoiceExtendedFields,
  InvoiceStatus,
  TInvoice,
  TInvoiceOpportunityStatus,
} from '@/types';
import type { TestScope } from '@/models/offers';

const callForJson = async <T,>(endpoint: string, json?: any): Promise<T> => {
  return call<T>(endpoint, { json }).then((res) => res.json());
};

export interface InvoiceStatusCountsResponse {
  all: number;
  issued: number;
  'confirmed-by-client': number;
  paid: number;
  'partially-paid': number;
  cancelled: number;
  test: number;
}

export const getInvoiceStatusCounts = async (
  filters?: SearchFilters,
): Promise<InvoiceStatusCountsResponse> => {
  return call<InvoiceStatusCountsResponse>('invoices/status-counts', {
    method: 'GET',
    json: (filters || {}) as any,
  }).then((res) => res.json());
};

export interface SearchFilters {
  invoiceNumber?: string;
  clientName?: string;
  clientEmail?: string;
  amount?: number;
  currency?: string;
  status?: InvoiceStatus;
  dateFrom?: Date;
  dateTo?: Date;
  source?: 'offer' | 'manual' | 'all';
  scope?: TestScope;
}

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: InvoiceStatus | 'all';
  filters?: SearchFilters;
  source?: 'offer' | 'manual' | 'all';
  scope?: TestScope;
}

export interface InvoiceListResponse {
  invoices: TInvoice[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface InvoiceDetails extends TInvoice, InvoiceExtendedFields {}

/** One entry in an invoice's payment history. */
export interface PaymentMovement {
  id: string;
  amount: number;
  collectedAt: string;
  recordedAt: string;
  recordedByEmail: string | null;
}

export interface RecordMovementRequest {
  id: string;
  /** The signed change: a collection is positive, a deduction negative. Never a total. */
  amount: number;
  /** ISO date the money actually moved (bank/wallet), not the entry date. */
  collectedAt: string;
  /**
   * The movement count this screen was showing. The server refuses the write if a
   * colleague's entry has landed since, so an accountant can never record against a
   * collected total they were not actually looking at.
   */
  expectedPaymentSeq: number;
}

export interface PaymentWriteResponse {
  invoice: TInvoice;
  paymentSeq: number;
  payments: PaymentMovement[];
}

/** The 409 body when someone else moved the invoice first. */
export interface InvoiceChangedBody {
  code: 'INVOICE_CHANGED';
  error: string;
  invoiceId: number;
  status: InvoiceStatus;
  paidAmount: number;
  paymentSeq: number;
  payments: PaymentMovement[];
}

export interface PaymentConfirmationRequest {
  id: string;
  paymentMethod: TInvoice['paymentMethod'];
  transactionHash?: string;
  fileId?: string;
  notes?: string;
}

export const keys = {
  all: ['invoices'] as const,
  list: (params?: InvoiceListParams) => [...keys.all, 'list', params] as const,
  details: (id: string) => [...keys.all, 'details', id] as const,
  search: (filters?: SearchFilters) =>
    [...keys.all, 'search', filters] as const,
  statusCounts: (filters?: SearchFilters) =>
    [...keys.all, 'statusCounts', filters] as const,
};

export const invalidateInvoiceQueries = (invoiceId?: string): void => {
  qc.invalidateQueries({ queryKey: keys.all });

  if (invoiceId) {
    qc.invalidateQueries({ queryKey: keys.details(invoiceId) });
  }
};

export const useCreateOpportunity = () => {
  return useMutation(
    {
      mutationFn: (id: number) =>
        call<void>('invoices/create-opportunity', { json: { id } }),
      onSuccess: (_, id) => {
        qc.setQueryData(keys.details(String(id)), (old: InvoiceDetails) => {
          return {
            ...old,
            opportunityStatus: TInvoiceOpportunityStatus.Queued,
          };
        });
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );
};

export const useInvoiceList = (params?: InvoiceListParams) => {
  return useQuery(
    {
      queryKey: keys.list(params),
      queryFn: () => {
        const { filters, ...rest } = params ?? {};
        const tab = rest.status && rest.status !== 'all' ? rest.status : '';
        const requestParams = {
          ...filters,
          ...rest,
          status: tab || filters?.status || undefined,
        };
        return call<InvoiceListResponse>('invoices/list', {
          method: 'GET',
          json: requestParams as any,
        }).then((res) => res.json());
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
    qc,
  );
};

export const useInvoiceSearch = (filters?: SearchFilters) => {
  return useQuery(
    {
      queryKey: keys.search(filters),
      queryFn: () =>
        call<InvoiceListResponse>('invoices/search', {
          method: 'GET',
          json: { filters } as any,
        }).then((res) => res.json()),
      enabled: !!filters && Object.keys(filters).length > 0,
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
    qc,
  );
};

export const useInvoiceStatusCounts = (filters?: SearchFilters) => {
  return useQuery(
    {
      queryKey: keys.statusCounts(filters),
      queryFn: () => getInvoiceStatusCounts(filters),
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
    },
    qc,
  );
};

/** Opportunity creation is queued work; these states resolve without another click. */
const OPPORTUNITY_IN_FLIGHT: (string | null | undefined)[] = [
  TInvoiceOpportunityStatus.Queued,
  TInvoiceOpportunityStatus.Processing,
];

export const useInvoiceDetails = (id: string | number) => {
  return useQuery(
    {
      queryKey: keys.details(id.toString()),
      queryFn: () => callForJson<InvoiceDetails>('invoices/details', { id }),
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      // The queue, not the click, decides when the opportunity exists. Without this
      // the button would sit at "Creating..." until the user reloaded the page.
      refetchInterval: (q) =>
        OPPORTUNITY_IN_FLIGHT.includes(
          (q.state.data as InvoiceDetails | undefined)?.opportunityStatus,
        )
          ? 5000
          : false,
    },
    qc,
  );
};

const UNSETTLED = ['queued', 'submission_unknown'];

export const useInvoiceDetailsByOfferId = (id: string | number) => {
  return useQuery(
    {
      queryKey: ['invoices', 'details-by-offer', id],
      queryFn: () =>
        callForJson<{ invoices: InvoiceDetails[] }>(
          'invoices/details-by-offer',
          { id },
        ).then((res) => res.invoices),
      enabled: !!id,
      refetchInterval: (q) =>
        (q.state.data as InvoiceDetails[] | undefined)?.some((i) =>
          UNSETTLED.includes(i.latestEmail?.status ?? ''),
        )
          ? 15000
          : false,
    },
    qc,
  );
};

export const useUpdatePaymentConfirmation = () => {
  return useMutation(
    {
      mutationFn: async (request: PaymentConfirmationRequest) => {
        // Transform request to match backend schema
        const backendRequest = {
          id: parseInt(request.id),
          transactionHash: request.transactionHash || null,
          fileId: request.fileId ? parseInt(request.fileId) : null,
        };

        await callForJson<{ success: boolean }>(
          'invoices/update-payment-confirmation',
          backendRequest,
        );
        return request.id; // Return the invoice ID for invalidation
      },
      onSuccess: (invoiceId) => {
        // Invalidate queries to trigger refetching of updated data
        invalidateInvoiceQueries(invoiceId);
      },
    },
    qc,
  );
};

/**
 * Fetches one invoice from the server and hands back what it now holds.
 *
 * A caller that has just been refused because its view of the invoice went stale cannot act
 * on an invalidation alone: it is still holding the figures that were rejected, and would
 * resubmit them unchanged. This waits for the truth and returns it.
 *
 * It asks the server outright rather than going through `refetchQueries`, which only refetches
 * queries that happen to be cached and mounted and swallows what it cannot do - so a caller
 * reading the cache afterwards could be handed the very staleness it was trying to escape, and
 * told it had been refreshed. This either returns current data or throws.
 */
export const refetchInvoiceDetails = async (
  id: string,
): Promise<InvoiceDetails> => {
  const fresh = await callForJson<InvoiceDetails>('invoices/details', { id });
  qc.setQueryData(keys.details(id), fresh);
  invalidateInvoiceQueries(id);
  return fresh;
};

// Opts a mutation out of the global "Operation Failed" toast (see lib/query.ts), for callers
// that render the failure themselves.
const markHandled = (error: unknown) => {
  if (error && typeof error === 'object') {
    (error as { handled?: boolean }).handled = true;
  }
};

/**
 * Records one movement against an invoice.
 *
 * The payload is the change, not the running total, so two accountants can no longer
 * overwrite each other by both stating what they believe the invoice holds. What stops the
 * second of them is `expectedPaymentSeq`: their view stopped being true the moment the first
 * entry landed, and the server says so rather than quietly adding to a figure they never saw.
 *
 * `retry: false` is deliberate. A movement is not idempotent - resending it records the money
 * twice unless the server happens to refuse it - so an automatic retry is exactly the wrong
 * instinct here.
 */
export const useRecordMovement = () => {
  return useMutation(
    {
      retry: false,
      mutationFn: (request: RecordMovementRequest) =>
        callForJson<PaymentWriteResponse>('invoices/record-payment', {
          ...request,
          id: Number(request.id),
        }),
      onSuccess: (res) => {
        qc.setQueryData(
          keys.details(res.invoice.id.toString()),
          (old: InvoiceDetails) => ({
            ...old,
            ...res.invoice,
            payments: res.payments,
            paymentSeq: res.paymentSeq,
          }),
        );
        invalidateInvoiceQueries(res.invoice.id.toString());
      },
      // The modal renders the refusal inline, beside the figures that caused it and the
      // history the accountant has to read. A generic red toast beside that says less.
      onError: markHandled,
    },
    qc,
  );
};

/**
 * Declares an invoice settled. Carries no amount and no staleness token: what was collected is
 * already recorded, and a second settlement is refused by the server's own status check.
 */
export const useSettleInvoice = () => {
  return useMutation(
    {
      retry: false,
      mutationFn: (id: string) =>
        callForJson<PaymentWriteResponse>('invoices/settle', {
          id: Number(id),
        }),
      onSuccess: (res) => {
        qc.setQueryData(
          keys.details(res.invoice.id.toString()),
          (old: InvoiceDetails) => ({
            ...old,
            ...res.invoice,
            payments: res.payments,
            paymentSeq: res.paymentSeq,
          }),
        );
        invalidateInvoiceQueries(res.invoice.id.toString());
      },
      onError: markHandled,
    },
    qc,
  );
};

export const useDownloadInvoicePDF = () => {
  return useMutation(
    {
      mutationFn: async (invoiceId: string | number) => {
        const response = await call('invoices/download-pdf', {
          method: 'GET',
          json: { id: invoiceId } as any,
        });

        if (!response.ok) {
          throw new Error('Failed to download PDF');
        }

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `invoice-${invoiceId}.pdf`; // fallback filename

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { success: true };
      },
    },
    qc,
  );
};

// Helper function to get status counts from invoice list
export const getStatusCounts = (invoices: TInvoice[]) => {
  const counts = {
    all: invoices.length,
    [InvoiceStatus.ISSUED]: 0,
    [InvoiceStatus.CONFIRMED_BY_CLIENT]: 0,
    [InvoiceStatus.PAID]: 0,
    [InvoiceStatus.PARTIALLY_PAID]: 0,
    [InvoiceStatus.CANCELLED]: 0,
  };

  invoices.forEach((invoice) => {
    counts[invoice.status]++;
  });

  return counts;
};

// Helper function to validate status transitions
export const getValidStatusTransitions = (
  currentStatus: InvoiceStatus,
): InvoiceStatus[] => {
  const validTransitions: Record<InvoiceStatus, InvoiceStatus[]> = {
    [InvoiceStatus.ISSUED]: [
      InvoiceStatus.ISSUED,
      InvoiceStatus.CONFIRMED_BY_CLIENT,
      InvoiceStatus.PARTIALLY_PAID,
      InvoiceStatus.PAID,
      InvoiceStatus.CANCELLED,
    ],
    [InvoiceStatus.CONFIRMED_BY_CLIENT]: [
      InvoiceStatus.CONFIRMED_BY_CLIENT,
      InvoiceStatus.PARTIALLY_PAID,
      InvoiceStatus.PAID,
      InvoiceStatus.CANCELLED,
    ],
    [InvoiceStatus.PARTIALLY_PAID]: [
      InvoiceStatus.PARTIALLY_PAID,
      InvoiceStatus.PAID,
      InvoiceStatus.CANCELLED,
    ],
    [InvoiceStatus.PAID]: [
      InvoiceStatus.PAID,
      InvoiceStatus.ISSUED,
      InvoiceStatus.CONFIRMED_BY_CLIENT,
      InvoiceStatus.PARTIALLY_PAID,
      InvoiceStatus.CANCELLED,
    ],
    [InvoiceStatus.CANCELLED]: [InvoiceStatus.CANCELLED],
  };

  return validTransitions[currentStatus] || [currentStatus];
};

export const useDownloadDocument = () => {
  return useMutation(
    {
      mutationFn: async (documentId: string | number) => {
        const response = await call('document/download', {
          method: 'POST',
          json: { id: parseInt(documentId.toString()) },
        });

        if (!response.ok) {
          throw new Error('Failed to download document');
        }

        // Get content type from response
        const contentType =
          response.headers.get('Content-Type') || 'application/octet-stream';

        // Generate filename based on content type
        let filename = `document-${documentId}`;
        if (contentType.includes('pdf')) {
          filename += '.pdf';
        } else if (contentType.includes('image')) {
          filename += '.jpg';
        } else if (contentType.includes('text')) {
          filename += '.txt';
        } else {
          filename += '.bin';
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { success: true };
      },
    },
    qc,
  );
};

export const useResendInvoice = (id: number) => {
  return useMutation(
    {
      mutationFn: () => call('invoices/resend', { json: { id } }),
    },
    qc,
  );
};

export const useApplyDiscount = () => {
  return useMutation(
    {
      mutationFn: (request: {
        invoiceId: number;
        discountPercentage: number;
      }) => callForJson<TInvoice>('invoices/apply-discount', request),
      onSuccess: (updatedInvoice) => {
        // Update the specific invoice in the cache
        qc.setQueryData(
          keys.details(updatedInvoice.id.toString()),
          (old: InvoiceDetails) => {
            return { ...old, ...updatedInvoice };
          },
        );

        qc.invalidateQueries({ queryKey: ['invoices', 'details-by-offer'] });

        // Invalidate list queries to refresh the main table
        invalidateInvoiceQueries(updatedInvoice.id.toString());
      },
    },
    qc,
  );
};

// Manual Invoice Creation Types
export interface ManualInvoicePayload {
  paymentMethod: string;
  invoiceCurrency: string;
  recipientType: string;
  recipientDetails:
    | {
        fullName: string;
        documentNumber: string;
        nationality: string;
        residenceCountry: string;
        legalAddress: string;
      }
    | {
        companyName: string;
        companyRegNumber: string;
        companyTaxId: string;
        companyLegalAddress: string;
      };
  services: Array<{
    serviceId: number;
    price: number;
    currency: string;
    quantity: number;
  }>;
  invoicingCompanyId: number | null;
  totals: {
    dueAmount: number;
    dueCurrency: string;
    total: number;
    invoiceCurrency: string;
  } | null;
}

export const useCreateManualInvoice = () => {
  return useMutation(
    {
      mutationFn: (payload: ManualInvoicePayload) =>
        callForJson<TInvoice>('invoices/create-manual', payload),
      onSuccess: () => {
        invalidateInvoiceQueries();
      },
    },
    qc,
  );
};

export const useSaveInvoiceDraft = () => {
  return useMutation(
    {
      mutationFn: (payload: ManualInvoicePayload) =>
        callForJson<{ success: boolean; draftId: number }>(
          'invoices/save-draft',
          payload,
        ),
      onSuccess: () => {
        invalidateInvoiceQueries();
      },
    },
    qc,
  );
};

export type TSplitPart = {
  id: number;
  invoiceNumber: string | null;
  partNumber: number | null;
  status: string;
  payerId: number | null;
  currency: string;
  dueAmount: number;
  dueAt: string | null;
  paymentMethod: string;
  coverageItemIds: string[];
};

export const splitKeys = {
  state: (offerId: number) => ['offers', 'split-state', offerId] as const,
};

export type TAmendPayerOption = {
  id: number;
  name: string;
  countryCode: string;
  clientType: string;
  eligible: boolean;
  blockReason: string | null;
  blockText: string | null;
  kycExpiresAt: string | null;
  email: string | null;
};

export type TAmendIssuerOption = {
  id: number;
  name: string;
  countryCode: string;
  priority: number;
  eligible: boolean;
  /**
   * R-1/R-3 are self-dealing rules an override permission can lift. RAIL means
   * the company holds no bank account in the chosen currency, or no wallet for
   * a crypto invoice - a fact about the company that nothing can override.
   */
  blockRule: 'R-1' | 'R-3' | 'RAIL' | null;
  blockText: string | null;
  /** What VAT this company would produce for the buyer under consideration. */
  vatRate?: number;
  /**
   * False where the company levies no VAT at all. A rate of zero says nothing
   * on its own - an exported supply is zero-rated and prints that zero - so
   * the VAT controls are driven by this, never by the rate.
   */
  vatApplicable?: boolean;
  vatNote?: string | null;
};

export type TAmendOptions = {
  amendable: boolean;
  status: string;
  mayOverrideSelfDealing: boolean;
  requiresExplicitCurrency: boolean;
  current: {
    payerId: number | null;
    paymentMethod: string;
    currency: string;
    bankCurrency: string | null;
    invoicingCompanyId: number | null;
    vatRate: number | null;
    vatNote: string | null;
    vatOverrideReason: string | null;
    vatOverridden: boolean;
    partNumber: number | null;
  };
  payers: TAmendPayerOption[];
  issuers: TAmendIssuerOption[];
  automaticVat: {
    vatRate: number;
    vatNote: string | null;
    buyerCountry: string;
    buyerType: string;
    buyerVatRegistered: boolean | null;
    companyCountry: string;
    vatApplicable?: boolean;
  } | null;
  planItems: Array<{
    seq: number;
    invoiceId: number | null;
    invoiceNumber: string | null;
    status: string | null;
    amount: number | null;
  }>;
};

export type TAmendRequest = {
  id: number;
  payerId?: number;
  paymentMethod?: string;
  currency?: string;
  invoicingCompanyId?: number;
  overrideSelfDealing?: boolean;
  vat?: {
    /** A fraction, not a percentage: 0.22 is 22%. */
    rate: number;
    reason: string;
  };
  revertVatToAutomatic?: boolean;
  /**
   * Consent to drop a standing manual VAT rate when moving the invoice to an
   * issuing company that levies no VAT. Without it that amendment is refused,
   * so an accountant's rate is never erased as a side effect.
   */
  clearVatOverride?: boolean;
  dueAt?: string;
  reason?: string;
  /** Compute the outcome and roll back, to show real numbers before committing. */
  dryRun?: boolean;
};

export type TAmendedInvoice = {
  id: number;
  invoiceNumber: string;
  partNumber: number | null;
  documentVersion: number;
  subtotalAmount: number;
  vatRate: number;
  vatAutomaticRate: number;
  vatOverridden: boolean;
  vatAmount: number;
  totalAmount: number;
  /** What the client owes once any discount is taken off. */
  payableAmount: number;
  currency: string;
};

export type TAmendResult = {
  amended: TAmendedInvoice[];
  inheritedPlanItemIds: number[];
  skipped: Array<{ seq: number; invoiceId: number; status: string }>;
};

/**
 * Eligibility for the amend form. Re-fetched as the accountant changes payer or
 * payment method, because both move which companies may legally issue.
 */
export const useAmendOptions = (
  id: number,
  probe?: {
    payerId?: number;
    paymentMethod?: string;
    currency?: string;
    invoicingCompanyId?: number;
  },
  enabled = true,
) => {
  return useQuery(
    {
      queryKey: ['invoices', 'amend-options', id, probe] as const,
      queryFn: () =>
        callForJson<TAmendOptions>('invoices/amend-options', {
          id,
          ...(probe ?? {}),
        }),
      enabled: enabled && !!id,
      staleTime: 0,
      // Each probe is a new query key. Without this the previous answer is
      // dropped the instant the payer or issuer changes, the form unmounts, and
      // the modal collapses to its header before springing back - a visible
      // flicker on every keystroke-sized change.
      placeholderData: keepPreviousData,
    },
    qc,
  );
};

export const useAmendInvoice = (offerId?: number) => {
  return useMutation(
    {
      mutationFn: (body: TAmendRequest) =>
        callForJson<TAmendResult>('invoices/amend', body),
      onSuccess: (_r, body) => {
        qc.invalidateQueries({ queryKey: keys.details(String(body.id)) });
        qc.invalidateQueries({ queryKey: ['invoices', 'amend-options'] });
        if (offerId) {
          qc.invalidateQueries({ queryKey: splitKeys.state(offerId) });
          qc.invalidateQueries({
            queryKey: ['invoices', 'details-by-offer', offerId],
          });
        }
      },
    },
    qc,
  );
};

/** Sending is a deliberate act now: amending never notifies anybody. */
export const useSendInvoice = () => {
  return useMutation(
    {
      mutationFn: (body: { id: number; message?: string }) =>
        callForJson<{ resent: boolean; resentTo?: string; resentAt?: string }>(
          'invoices/resend',
          body,
        ),
      onSuccess: (_r, body) => {
        qc.invalidateQueries({ queryKey: keys.details(String(body.id)) });
      },
    },
    qc,
  );
};

export function useCancelInvoicePart(offerId: number) {
  return useMutation(
    {
      mutationFn: (id: number) =>
        call('invoices/cancel', { json: { id } as any }).then((r) => r.json()),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: splitKeys.state(offerId) });
        qc.invalidateQueries({
          queryKey: ['invoices', 'details-by-offer', offerId],
        });
      },
    },
    qc,
  );
}

export function useInvoiceKycSession() {
  return useMutation(
    {
      mutationFn: (body: { invoiceId: number; payerId?: number }) =>
        call('invoices/kyc-session', { json: body as any }).then(
          (r) =>
            r.json() as Promise<{
              sessionId: number;
              url: string;
              reverify: boolean;
            }>,
        ),
    },
    qc,
  );
}
